import type { WidgetInstance } from "../../shared/layout";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";
import { listWidgets, getWidget } from "../widgets/registry";
import { resolveConfig, type SettingField, type WidgetDefinition } from "../widgets/types";

const CATEGORIES: { key: WidgetDefinition["category"]; label: string }[] = [
  { key: "time", label: "Time & today" },
  { key: "calendar", label: "Calendar" },
  { key: "household", label: "Family & home" },
  { key: "weather", label: "Weather, sky & nature" },
];

export function AddWidgetSheet({ onPick, onClose }: { onPick: (def: WidgetDefinition) => void; onClose: () => void }) {
  return (
    <Sheet title="Add a widget" onClose={onClose} size="lg">
      {CATEGORIES.map((cat) => {
        const defs = listWidgets().filter((d) => d.category === cat.key);
        if (defs.length === 0) return null;
        return (
          <section key={cat.key} className="catalog-group">
            <h3>{cat.label}</h3>
            <div className="widget-catalog">
              {defs.map((def) => (
                <button key={def.type} className="catalog-item" onClick={() => onPick(def)}>
                  <span className="catalog-icon">
                    <Icon name={def.icon} size="1.7rem" />
                  </span>
                  <span className="catalog-text">
                    <strong>{def.name}</strong>
                    <span className="muted">{def.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        );
      })}
      <p className="muted small">You can add the same widget more than once — each copy has its own settings, and they share data.</p>
    </Sheet>
  );
}

/** Edits apply to the layout draft immediately, so the widget previews live behind the sheet. */
export function WidgetSettingsSheet({ item, onChange, onClose }: { item: WidgetInstance; onChange: (config: Record<string, unknown>) => void; onClose: () => void }) {
  const def = getWidget(item.type);
  if (!def) return null;
  const config = resolveConfig(def, item.config, item.version) as Record<string, unknown>;
  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });
  return (
    <Sheet
      title={`${def.name} settings`}
      onClose={onClose}
      size="sm"
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="form">
        {def.settings.map((f) => (
          <SettingControl key={f.key} field={f as SettingField<Record<string, unknown>>} value={config[f.key]} onChange={(v) => set(f.key, v)} />
        ))}
      </div>
    </Sheet>
  );
}

function SettingControl({ field, value, onChange }: { field: SettingField<Record<string, unknown>>; value: unknown; onChange: (v: unknown) => void }) {
  switch (field.type) {
    case "select":
      return (
        <div className="field">
          <span>{field.label}</span>
          <div className="chip-row wrap">
            {field.options.map((o) => (
              <button key={String(o.value)} className={`chip ${value === o.value ? "on" : ""}`} onClick={() => onChange(o.value)}>
                {o.label}
              </button>
            ))}
          </div>
          {field.help && <p className="muted small">{field.help}</p>}
        </div>
      );
    case "toggle":
      return (
        <label className="field field-toggle">
          <span>
            {field.label}
            {field.help && <small className="muted">{field.help}</small>}
          </span>
          <input type="checkbox" className="switch" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        </label>
      );
    case "number":
      return (
        <label className="field">
          <span>
            {field.label}: <strong>{String(value)}</strong>
          </span>
          <input type="range" min={field.min} max={field.max} step={field.step ?? 1} value={Number(value)} onChange={(e) => onChange(Number(e.target.value))} />
          {field.help && <p className="muted small">{field.help}</p>}
        </label>
      );
    case "text":
      return (
        <label className="field">
          <span>{field.label}</span>
          <input className="input" value={String(value ?? "")} maxLength={field.maxLength} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
  }
}
